import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './tooltip.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { generateID } from 'elements/_shared/generate-id.js'

export const tooltipPositions = ['top', 'right', 'bottom', 'left'] as const
export type tooltipPositionTypes = typeof tooltipPositions

@customElement('cse-tooltip')
export class CSETooltip extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: Boolean, reflect: true })
  open = false

  @property({ type: String })
  position: tooltipPositionTypes[number] = 'top'

  @state()
  private displayTimeout: ReturnType<typeof setTimeout> = setTimeout(() => {}, 0)

  private tooltipId = generateID('tooltip')

  private tooltipDistance = 10

  tooltipDisplayChanged: Promise<void> = Promise.resolve()

  private delay = (time: number) => {
    let resolveTooltipDisplay!: () => void

    this.tooltipDisplayChanged = new Promise((resolve) => {
      resolveTooltipDisplay = resolve

      this.displayTimeout = setTimeout(() => {
        resolveTooltipDisplay()
      }, time)
    })

    return this.tooltipDisplayChanged
  }

  private openTooltip() {
    clearTimeout(this.displayTimeout)
    this.delay(200).then(() => this.open = true)
  }

  private closeTooltip() {
    clearTimeout(this.displayTimeout)
    this.delay(200).then(() => this.open = false)
  }

  override render() {
    return html`
      <cse-popup
        class="${classMap({
          'cse-tooltip': true,
          [`position-${this.position}`]: true,
        })}"
        ?active="${this.open}"
        position="${this.position}"
        distance="${this.tooltipDistance}"
      >
        <slot
          name="anchor"
          slot="anchor"
          @mouseover="${this.openTooltip}"
          @mouseout="${this.closeTooltip}"
          @focus="${this.openTooltip}"
          @blur="${this.closeTooltip}"
          aria-describedby="${this.tooltipId}-content"
        ></slot>

        <div
          id="${this.tooltipId}-content"
          class="tooltip font-caption-l"
          role="tooltip"
          aria-hidden="${!this.open}"
        >
          <slot></slot>
        </div>

        <div class="tooltip-arrow"></div>
      </cse-popup>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-tooltip': CSETooltip
  }
}
